import React, { useState, useEffect } from 'react';
import MaintenanceMode from './MaintenanceMode';
import MaintenanceLoginForm from './MaintenanceLoginForm';

interface MaintenanceGateProps {
  children: React.ReactNode;
}

const MaintenanceGate: React.FC<MaintenanceGateProps> = ({ children }) => {
  // Check if maintenance mode is enabled
  const isMaintenanceModeEnabled = import.meta.env.VITE_MAINTENANCE_MODE === 'true';
  
  // Hardcoded credentials for simplicity - in production, these would be more secure
  const VALID_CREDENTIALS = {
    username: 'admin',
    password: 'tvs2025-admin-717!'
  };
  
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);
  
  // Check if user is already authenticated from localStorage
  useEffect(() => {
    const authStatus = localStorage.getItem('tvs-maintenance-auth');
    const authTimestamp = localStorage.getItem('tvs-maintenance-auth-time');
    
    if (authStatus === 'true' && authTimestamp) {
      // Check if auth is less than 24 hours old
      const authTime = parseInt(authTimestamp);
      const now = Date.now();
      const hoursDifference = (now - authTime) / (1000 * 60 * 60);
      
      if (hoursDifference < 24) {
        setIsAuthenticated(true);
      } else {
        // Auth expired
        localStorage.removeItem('tvs-maintenance-auth');
        localStorage.removeItem('tvs-maintenance-auth-time');
      }
    }
  }, []);
  
  const handleLogin = (username: string, password: string): boolean => {
    if (username === VALID_CREDENTIALS.username && password === VALID_CREDENTIALS.password) {
      setIsAuthenticated(true);
      localStorage.setItem('tvs-maintenance-auth', 'true');
      localStorage.setItem('tvs-maintenance-auth-time', Date.now().toString());
      return true;
    }
    return false;
  };
  
  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('tvs-maintenance-auth');
    localStorage.removeItem('tvs-maintenance-auth-time');
    setShowLoginForm(false);
  };
  
  // If maintenance mode is not enabled, show the app normally
  if (!isMaintenanceModeEnabled) {
    return <>{children}</>;
  }
  
  // If user is authenticated, show the app
  if (isAuthenticated) {
    return (
      <div className="relative">
        {/* Maintenance mode indicator for authenticated users */}
        <div className="fixed top-0 left-0 right-0 bg-orange-500 text-white text-center py-1 text-sm z-[9999]">
          🔧 Maintenance Mode Active - 
          <button 
            onClick={handleLogout}
            className="ml-2 underline hover:no-underline"
          >
            Logout
          </button>
        </div>
        <div className="pt-8">
          {children}
        </div>
      </div>
    );
  }
  
  // Show login form if requested
  if (showLoginForm) {
    return (
      <MaintenanceLoginForm 
        onLogin={handleLogin}
        onCancel={() => setShowLoginForm(false)}
      />
    );
  }
  
  // Show maintenance mode page with hidden login option
  return (
    <MaintenanceMode 
      onShowLogin={() => setShowLoginForm(true)} 
    />
  );
};

export default MaintenanceGate; 